/* eslint-disable no-console */
import axios from 'axios';
import * as BandwidthUA from '@bandwidth/bw-webrtc-sdk';
import { CALL_STATUS_ACTIVE } from '../constants/RhinocallConstants';
import store from '../store';
import { updateCallOrCancel, handleCompleteCall, setDisplay } from '../reducers/rhinocallReducer';
import { PhoneDigits } from '../helpers/PhoneHelpers';

const activePhoneSession = {
  phone: null,
  call: null,
  sessionId: null,
  callRecord: null,
  iceServers: null,
  turnExpiresAt: 0,
  forceRelay: false,
};

// ---------------------------------------------------------------------------
// TURN / ICE resilience
//
// The served iceServers include a TURN relay (turns:turn1.<zone>) minted by
// rhinocall with a 300s HMAC credential. Three things keep relayed calls alive:
//  1. force-relay shim: the Bandwidth SDK exposes no iceTransportPolicy, so for
//     orgs flagged forceRelay we wrap RTCPeerConnection and inject 'relay' —
//     but ONLY for configs carrying our TURN host, so other WebRTC users in the
//     app (RhinoVideo/Twilio) are untouched.
//  2. credential refresh: the TURN cred outlives few calls; refresh it before
//     any ICE restart so re-gathering can authenticate against the pool.
//  3. ICE restart + RTP watchdog: when the relaying TURN box dies the browser
//     takes 5-10s to declare 'failed'; a flat inbound-RTP counter triggers the
//     restart in ~4s. restartIce() + a hold/unhold re-INVITE re-gathers on a
//     surviving box. Planned TURN scale-in never needs this (boxes drain).
// ---------------------------------------------------------------------------
const RHINO_TURN_URL_PREFIX = 'turns:turn1.';
const TURN_REFRESH_SKEW_MS = 60 * 1000;
const RTP_WATCHDOG_INTERVAL_MS = 2000;
const RTP_STALL_CHECKS = 2; // ~4s of no inbound audio packets
const ICE_DISCONNECTED_GRACE_MS = 3000;
const ICE_RECOVERY_COOLDOWN_MS = 15000;

const iceRecovery = {
  watchdog: null,
  lastPacketsReceived: -1,
  stalledChecks: 0,
  disconnectedTimer: null,
  inProgress: false,
  lastAttemptAt: 0,
};

function isRhinoTurnConfig(config) {
  return !!config?.iceServers?.some((server) => [].concat(server?.urls || [])
    .some((url) => typeof url === 'string' && url.startsWith(RHINO_TURN_URL_PREFIX)));
}

function installForceRelayShim() {
  const Native = window.RTCPeerConnection;
  if (!Native || Native.__rhinocallRelayShim) return;
  function RhinocallRTCPeerConnection(config, ...rest) {
    const cfg = (activePhoneSession.forceRelay && isRhinoTurnConfig(config))
      ? { ...config, iceTransportPolicy: 'relay' }
      : config;
    return new Native(cfg, ...rest);
  }
  RhinocallRTCPeerConnection.prototype = Native.prototype;
  Object.setPrototypeOf(RhinocallRTCPeerConnection, Native); // statics (generateCertificate)
  RhinocallRTCPeerConnection.__rhinocallRelayShim = true;
  window.RTCPeerConnection = RhinocallRTCPeerConnection;
}

installForceRelayShim();

activePhoneSession.phone = new BandwidthUA.BandwidthUA();

function getActivePeerConnection() {
  try {
    return activePhoneSession.call?.getRTCPeerConnection?.() || null;
  } catch (e) {
    return null;
  }
}

function rememberIceServers(serverConfig) {
  activePhoneSession.iceServers = serverConfig.iceServers;
  activePhoneSession.forceRelay = !!serverConfig.forceRelay;
  const turn = serverConfig.iceServers?.find((s) => typeof s === 'object' && s?.ttl);
  activePhoneSession.turnExpiresAt = turn ? Date.now() + (turn.ttl * 1000) : 0;
}

async function refreshIceServersIfStale(force = false) {
  const { turnExpiresAt, phone } = activePhoneSession;
  if (!force && (!turnExpiresAt || Date.now() < turnExpiresAt - TURN_REFRESH_SKEW_MS)) return;
  const { data: { serverConfig } } = await getConnectionInfo();
  rememberIceServers(serverConfig);
  // Future calls on this phone
  phone.setServerConfig(serverConfig.addresses, serverConfig.domain, serverConfig.iceServers);
  // The in-flight call: swap creds on the live connection so re-gathering can auth
  const pc = getActivePeerConnection();
  if (pc) {
    pc.setConfiguration({ ...pc.getConfiguration(), iceServers: serverConfig.iceServers });
  }
}

async function selectedRelayAddress(pc) {
  try {
    const stats = await pc.getStats();
    let pair = null;
    stats.forEach((r) => {
      if (r.type === 'candidate-pair' && (r.nominated || r.state === 'succeeded') && !pair) pair = r;
    });
    const local = pair && stats.get(pair.localCandidateId);
    return local ? `${local.candidateType} ${local.address || local.ip}:${local.port}` : 'unknown';
  } catch (e) {
    return 'unknown';
  }
}

async function recoverIce(reason) {
  const { call } = activePhoneSession;
  const pc = getActivePeerConnection();
  if (!call || !pc || pc.connectionState === 'closed') return;
  if (iceRecovery.inProgress || Date.now() - iceRecovery.lastAttemptAt < ICE_RECOVERY_COOLDOWN_MS) return;
  iceRecovery.inProgress = true;
  iceRecovery.lastAttemptAt = Date.now();
  const path = await selectedRelayAddress(pc);
  console.warn(`[RhinoCall ICE] restart (${reason}) from ${path}`);
  try {
    await refreshIceServersIfStale();
    pc.restartIce();
    // The SDK has no renegotiate(); a hold/unhold re-INVITE carries the fresh
    // ICE credentials from restartIce() and completes the restart.
    if (!call.isRemoteHold?.() && !call.isLocalHold?.()) {
      await call.hold(true);
      await call.hold(false);
    }
    iceRecovery.stalledChecks = 0;
  } catch (e) {
    console.error('[RhinoCall ICE] restart failed', e);
  } finally {
    iceRecovery.inProgress = false;
  }
}

function stopIceRecovery() {
  clearInterval(iceRecovery.watchdog);
  clearTimeout(iceRecovery.disconnectedTimer);
  iceRecovery.watchdog = null;
  iceRecovery.disconnectedTimer = null;
  iceRecovery.lastPacketsReceived = -1;
  iceRecovery.stalledChecks = 0;
}

function startIceRecovery() {
  stopIceRecovery();
  const pc = getActivePeerConnection();
  if (!pc) return;

  pc.addEventListener('iceconnectionstatechange', () => {
    console.log('[RhinoCall ICE] state:', pc.iceConnectionState);
    clearTimeout(iceRecovery.disconnectedTimer);
    if (pc.iceConnectionState === 'failed') {
      recoverIce('ice failed');
    } else if (pc.iceConnectionState === 'disconnected') {
      iceRecovery.disconnectedTimer = setTimeout(() => {
        if (pc.iceConnectionState === 'disconnected') recoverIce('ice disconnected');
      }, ICE_DISCONNECTED_GRACE_MS);
    }
  });

  iceRecovery.watchdog = setInterval(async () => {
    if (pc.connectionState === 'closed') { stopIceRecovery(); return; }
    if (isCallHeld()) return;
    try {
      const stats = await pc.getStats();
      let packets = -1;
      stats.forEach((r) => {
        if (r.type === 'inbound-rtp' && r.kind === 'audio') packets = r.packetsReceived;
      });
      if (packets >= 0 && packets === iceRecovery.lastPacketsReceived) {
        iceRecovery.stalledChecks += 1;
        if (iceRecovery.stalledChecks >= RTP_STALL_CHECKS) recoverIce('inbound rtp stalled');
      } else {
        iceRecovery.stalledChecks = 0;
      }
      iceRecovery.lastPacketsReceived = packets;
    } catch (e) {
      // stats unavailable this tick; try again next interval
    }
  }, RTP_WATCHDOG_INTERVAL_MS);
}

function isCallHeld() {
  const { call } = activePhoneSession;
  return !!(call?.isLocalHold?.() || call?.isRemoteHold?.());
}

const extraHeaders = ['User-to-User:eyJhbGciOiJIUzI1NiJ9.WyJoaSJd.-znkjYyCkgz4djmHUPSXl9YrJ6Nix_XvmlwKGFh5ERM;encoding=jwt;aGVsbG8gd29ybGQ;encoding=base64'];
const audioStreamChangedSubscribers = new Map();
const callStateChangedSubscribers = new Map();

export async function startCall(payload, phone) {
  const callLog = await createSession(payload, phone);
  const { sessionId } = activePhoneSession;
  return { sessionId, callLog };
}

export async function endCall() {
  closeActiveSession();
}

export function muteAudio(value) {
  if (activePhoneSession && activePhoneSession.call) {
    activePhoneSession.call.muteAudio(value);
  }
}

export function isMuted() {
  if (activePhoneSession && activePhoneSession.call) {
    return activePhoneSession.call.isMuted();
  }
  return false;
}

export function sendDTMF(value) {
  if (activePhoneSession && activePhoneSession.call) {
    activePhoneSession.call.sendDTMF(value);
  }
}

export function registerAudioStreamChanged(consumerId, callback) {
  if (consumerId && callback) {
    if (!audioStreamChangedSubscribers.has(consumerId)) {
      audioStreamChangedSubscribers.set(consumerId, callback);
    }
  }
}

export function unregisterAudioStreamChanged(consumerId) {
  if (audioStreamChangedSubscribers.has(consumerId)) {
    audioStreamChangedSubscribers.delete(consumerId);
  }
}

function notifyAudioStreamChangedSubscribers(remoteStream) {
  audioStreamChangedSubscribers.forEach((callback) => callback(remoteStream));
}

export function registerCallStateChangedSubscribers(consumerId, callback) {
  if (consumerId && callback && !callStateChangedSubscribers.has(consumerId)) {
    callStateChangedSubscribers.set(consumerId, callback);
  }
}

export function unregisterCallStateChangedSubscribers(consumerId) {
  if (consumerId && callStateChangedSubscribers.has(consumerId)) {
    callStateChangedSubscribers.delete(consumerId);
  }
}

// function notifyCallStateChangedSubscribers(callState) {
//   callStateChangedSubscribers.forEach((callback) => callback(callState));
// }

export async function initSession(payload) {
  closeActiveSession();
  const { phone } = activePhoneSession;
  const connectionInfo = await getConnectionInfo();
  const { serverConfig, auth } = connectionInfo.data;
  rememberIceServers(serverConfig);
  phone.setWebSocketKeepAlive(5, false, false, 5, true);
  phone.setServerConfig(serverConfig.addresses,
    serverConfig.domain,
    serverConfig.iceServers);
  phone.setOAuthToken(auth.access_token);
  await phone.checkAvailableDevices();
  phone.setListeners({
    loginStateChanged,
    outgoingCallProgress,
    callTerminated,
    callConfirmed,
    callShowStreams,
    callHoldStateChanged,
    incomingCall,
  });
  console.log('*******initSession', payload); // eslint-disable-line no-console
  phone.setAccount(`${payload.fromNumber}`, 'Rhinogram', '');
  await phone.init();
  return phone;
}

async function createSession(payload, phone) {
  activePhoneSession.phone = phone;
  let callLog = null;
  if (phone && phone.isInitialized) {
    console.log('*******isInitializedphone', phone); // eslint-disable-line no-console
    console.log('*****isInitializedpayload', payload); // eslint-disable-line no-console
    callLog = await createCall(payload);
    // BW required phone number to be in E.164 format without + sign
    const toPhoneDigitsOnly = PhoneDigits(payload.toNumber);

    // initSession may have run a while ago; don't dial with an expiring TURN cred
    await refreshIceServersIfStale().catch((e) => console.warn('[RhinoCall ICE] cred refresh failed', e));
    activePhoneSession.call = await phone.makeCall(`${toPhoneDigitsOnly}`, extraHeaders);
    activePhoneSession.sessionId = callLog.data._id;
    activePhoneSession.callRecord = callLog?.data;
  }
  return callLog;
}

async function closeActiveSession() {
  const { phone, call } = activePhoneSession;
  stopIceRecovery();
  activePhoneSession.forceRelay = false;
  if (call) {
    call.terminate();
  }
  if (phone && phone.isInitialized()) {
    phone.deinit();
  }
  activePhoneSession.call = null;
  activePhoneSession.sessionId = null;
  activePhoneSession.callRecord = null;
}

async function callShowStreams(call, localStream, remoteStream) {
  // The provided function is invoked when media streams have been established. This often corresponds with the answering of the call.
  // console.log('WOOT > callShowStreams', remoteStream);
  notifyAudioStreamChangedSubscribers(remoteStream);
  startIceRecovery();
  const callLog = await updateCall(activePhoneSession?.sessionId, { status: CALL_STATUS_ACTIVE });
  store.dispatch(updateCallOrCancel(callLog?.data));
}

function incomingCall(call, invite) {
  // NOT CURRENTLY USED
  console.log('WOOT > incomingCall', call, invite);
}

function loginStateChanged(isLogin, cause) {
  // NOT CURRENTLY USED
  // The provided function is invoked when the connection state between the Bandwidth WebRTC server and the Client changes.
  // This can occur when a requested connection is established in a bi-directional manner, or when that connection fails.
  // There are other causes of this event that will be documented elsewhere.
  console.log('WOOT > loginStateChanged', isLogin, cause);
  // possible causes are connected, disconnected, login failed, login, logout
}

function outgoingCallProgress(call, response) {
  // The provided function is invoked when there is a change in the state of the call from the client.
  // A classic example of this is when a ringing state is detected.
  console.log('WOOT > outgoingCallProgress', call, response);
  // notifyCallStateChangedSubscribers(CALL_STATUS_INITIATED);
}

async function callTerminated(call, message, cause) {
  stopIceRecovery();
  // await updateCall(activePhoneSession?.sessionId, { status: CALL_STATUS_COMPLETED, disconnectCause: cause });
  store.dispatch(setDisplay('complete'));
  store.dispatch(handleCompleteCall({ _id: activePhoneSession?.sessionId, disconnectCause: cause }));
}

async function callConfirmed(call, message) {
  const callLog = await updateCall(activePhoneSession?.sessionId, { callId: message?.call_id });
  store.dispatch(updateCallOrCancel(callLog?.data));
}

function callHoldStateChanged(call, isHold, isRemote) {
  // NOT CURRENTLY USED
  // If the system is configured to support hold state conditions, this function will be invoked on changes in the hold state of the call.
  console.log('WOOT > callHoldStateChanged', call, isHold, isRemote);
}

function getConnectionInfo() {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/calls/connectionInfo`);
}

function createCall(data) {
  return axios.post(`${process.env.REACT_APP_CALL_BASE_URL}/calls`, data);
}

export function updateCall(callId, data) {
  return axios.patch(`${process.env.REACT_APP_CALL_BASE_URL}/calls/${callId}`, data);
}

// legacy
export function postCall(data) {
  return axios.post(`${process.env.REACT_APP_CALL_BASE_URL}/calls`, data);
}

export function expireCall() {
  return axios.patch(`${process.env.REACT_APP_CALL_BASE_URL}/calls/expired`);
}

export function patchCall(callId, data) {
  return axios.patch(`${process.env.REACT_APP_CALL_BASE_URL}/calls/${callId}`, data);
}

export function getOrganization(organizationId) {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/org/${organizationId}`);
}

export function getOrganizationToken(organizationId) {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/org/${organizationId}/token`);
}

export function getCallsByStatus(filter) {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/calls?status${filter}`);
}

export function postOrganization(payload) {
  return axios.post(`${process.env.REACT_APP_CALL_BASE_URL}/org/${payload.organizationId}`, payload);
}

export function getLastCallByUser() {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/calls?latest=true`);
}

export function getCall(callLogId) {
  return axios.get(`${process.env.REACT_APP_CALL_BASE_URL}/calls/${callLogId}`);
}
